import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { PHOTOS } from "@/lib/site";

export default function PhotosPage() {
    const [activeIdx, setActiveIdx] = useState(null);
    const open = activeIdx !== null;

    const close = useCallback(() => setActiveIdx(null), []);
    const prev = useCallback(
        () => setActiveIdx((i) => (i === null ? null : (i - 1 + PHOTOS.length) % PHOTOS.length)),
        [],
    );
    const next = useCallback(
        () => setActiveIdx((i) => (i === null ? null : (i + 1) % PHOTOS.length)),
        [],
    );

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, close, prev, next]);

    return (
        <main data-testid="photos-page" className="relative z-10 pt-32 pb-24">
            {/* HEADER */}
            <section>
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="grid grid-cols-1 md:grid-cols-12 gap-10"
                    >
                        <div className="md:col-span-7">
                            <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                                ◇ Field Work
                            </div>
                            <h1 className="mt-4 font-display font-black text-white text-5xl md:text-7xl uppercase tracking-tighter leading-[0.92]">
                                From the
                                <br />
                                shop floor.
                            </h1>
                        </div>
                        <div className="md:col-span-5 flex md:items-end">
                            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                                A look at recent LMJ International work —
                                pipe inserts, structural assemblies, and
                                shipyard fabrication captured on job.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* GRID */}
            <section className="mt-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {PHOTOS.map((p, i) => (
                            <button
                                key={p.src}
                                data-testid={`photo-tile-${i}`}
                                onClick={() => setActiveIdx(i)}
                                className="group relative aspect-[4/3] overflow-hidden bg-brand-surface focus:outline-none focus:ring-2 focus:ring-brand-blue"
                            >
                                {p.type === "video" ? (
                                    <video
                                        src={p.src}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                ) : (
                                    <img
                                        src={p.src}
                                        alt={p.alt}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/90 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                                {p.type === "video" && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-brand-blue/90 flex items-center justify-center shadow-[0_6px_30px_rgba(0,85,164,0.5)] group-hover:scale-110 transition-transform">
                                            <Play className="h-6 w-6 md:h-7 md:w-7 text-white fill-white ml-1" />
                                        </div>
                                    </div>
                                )}
                                <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] text-white/80 uppercase bg-brand-bg/60 backdrop-blur-sm px-2 py-1">
                                    / {String(i + 1).padStart(2, "0")}
                                    {p.type === "video" && (
                                        <span className="ml-2 text-brand-blue">• VIDEO</span>
                                    )}
                                </div>
                                <div className="absolute bottom-4 left-4 right-4 translate-y-2 group-hover:translate-y-0 opacity-80 group-hover:opacity-100 transition-all duration-300">
                                    <div className="font-mono text-[10px] tracking-[0.2em] text-brand-blue uppercase">
                                        {p.type === "video" ? "Play" : "View"}
                                    </div>
                                    <div className="font-display font-bold text-white text-sm md:text-base uppercase tracking-tight leading-tight mt-1 line-clamp-2">
                                        {p.alt}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* LIGHTBOX */}
            {open && (
                <div
                    data-testid="photo-lightbox"
                    className="fixed inset-0 z-[100] bg-brand-bg/95 backdrop-blur-xl flex items-center justify-center p-4"
                    onClick={close}
                >
                    <button
                        data-testid="lightbox-close"
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                        }}
                        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 flex items-center justify-center text-white hover:bg-white/10 border border-white/20 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                    <button
                        data-testid="lightbox-prev"
                        onClick={(e) => {
                            e.stopPropagation();
                            prev();
                        }}
                        className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white hover:bg-white/10 border border-white/20 transition-colors"
                        aria-label="Previous photo"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        data-testid="lightbox-next"
                        onClick={(e) => {
                            e.stopPropagation();
                            next();
                        }}
                        className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white hover:bg-white/10 border border-white/20 transition-colors"
                        aria-label="Next photo"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                    <div
                        className="relative max-w-6xl w-full max-h-[88vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {PHOTOS[activeIdx].type === "video" ? (
                            <video
                                data-testid="lightbox-video"
                                src={PHOTOS[activeIdx].src}
                                controls
                                autoPlay
                                playsInline
                                className="w-full h-auto max-h-[82vh] object-contain bg-black"
                            />
                        ) : (
                            <img
                                data-testid="lightbox-image"
                                src={PHOTOS[activeIdx].src}
                                alt={PHOTOS[activeIdx].alt}
                                className="w-full h-auto max-h-[82vh] object-contain"
                            />
                        )}
                        <div className="mt-4 flex items-end justify-between gap-6">
                            <div className="font-mono text-xs tracking-[0.2em] text-slate-400 uppercase">
                                {PHOTOS[activeIdx].alt}
                            </div>
                            <div className="font-mono text-xs tracking-[0.2em] text-brand-blue uppercase shrink-0">
                                {String(activeIdx + 1).padStart(2, "0")} / {String(PHOTOS.length).padStart(2, "0")}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
